import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo, useRef } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { useAccount } from 'wagmi';
import { useHighlightedRows } from '../../hooks/useHighlightedRows';
import { ProductType } from '../../hooks/useProducts';
import { useScrolledToEnd } from '../../hooks/useScrolledToEnd';
import { useTrades } from '../../hooks/useTrades';
import { formatDate, formatNumber } from '../../lib/formatters';
import { getDecimalPlaces } from '../../lib/math';
import { OrderSide } from '../../types/generated';
import { StateTable } from '../Table/Table';
import { Tooltip } from '../Tooltip/Tooltip';

type TableData = {
  price: bigint;
  size: bigint;
  time: number;
  makerSide: OrderSide;
  id: string;
  sizeDecimals: number;
  priceDecimals: number;
};
export const TradesUi: React.FC<{
  tableData: TableData[];
  isPending: boolean;
  isFetching: boolean;
  isConnected: boolean;
  baseTokenName: string;
  quoteTokenName: string;
}> = ({ tableData, isFetching, isPending, isConnected, baseTokenName, quoteTokenName }) => {
  const newRowIds = useHighlightedRows(tableData);
  const tableRef = useRef<HTMLDivElement | null>(null);

  const isAtBottom = useScrolledToEnd(tableRef);

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<TableData>();
    return [
      columnHelper.accessor(
        ({ price, priceDecimals, makerSide }) => ({ price, priceDecimals, makerSide }),
        {
          id: 'price_with_decimals',
          header: `Price (${quoteTokenName})`,
          cell: (info) => {
            const { price, priceDecimals, makerSide } = info.getValue();
            return (
              <span
                className={makerSide === OrderSide.BUY ? 'text-contentGreen' : 'text-contentRed'}
              >
                {formatNumber(price, {
                  minimumFractionDigits: priceDecimals,
                  maximumFractionDigits: priceDecimals,
                })}
              </span>
            );
          },
          meta: {
            cellContainerClassName: 'text-nowrap text-2xs px-2 py-0.5',
            cellHeaderContainerClassName: 'px-2',
          },
        }
      ),
      columnHelper.accessor(({ size, sizeDecimals }) => ({ size, sizeDecimals }), {
        id: 'size_with_decimals',
        header: `Size (${baseTokenName})`,
        cell: (info) => {
          const { size, sizeDecimals } = info.getValue();
          return formatNumber(size, {
            minimumFractionDigits: sizeDecimals,
            maximumFractionDigits: sizeDecimals,
          });
        },
        meta: {
          cellContainerClassName: 'text-nowrap text-2xs px-2 py-0.5',
          cellHeaderContainerClassName: 'px-2',
        },
      }),

      columnHelper.accessor('time', {
        header: 'Time',
        cell: (info) => (
          <Tooltip placement="bottom" content={formatDate(info.getValue())}>
            {formatDate(info.getValue(), { dateStyle: undefined })}
          </Tooltip>
        ),
        meta: {
          align: 'right',
          cellContainerClassName: 'text-nowrap text-2xs px-2 py-0.5',
          cellHeaderContainerClassName: 'px-2',
        },
      }),
    ];
  }, [baseTokenName, quoteTokenName]);

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    meta: {
      getRowClassName(row) {
        if (!newRowIds.has(row.original.id)) {
          return 'border-none';
        }
        return twJoin(
          row.original.makerSide === OrderSide.BUY ? 'bg-green-500/10' : 'bg-red-500/10',
          'border-none'
        );
      },
    },
  });

  return (
    <div className="flex size-full flex-col max-lg:min-h-[600px]">
      <div className="relative flex-1">
        <div className="absolute inset-0">
          <StateTable
            ref={tableRef}
            table={table}
            className="h-full"
            mobileLayout="table"
            isPending={isPending}
            isFetching={isFetching}
            isConnected={isConnected}
            requireConnected={false}
            noDataText="No trades on this market yet."
            skeletonRowClassName={'h-5'}
            showPaginationControls={false}
          />
          <div
            className={twMerge(
              'pointer-events-none absolute -bottom-px h-[44px] w-full bg-gradient-to-b from-transparent to-backgroundBase transition-opacity',
              isAtBottom ? 'opacity-0' : 'opacity-100'
            )}
          />
        </div>
      </div>
    </div>
  );
};

export const Trades: React.FC<{ product: ProductType }> = ({ product }) => {
  const { isConnected } = useAccount();
  const { data: trades, isFetching, isPending } = useTrades({ productId: product.id, limit: 200 });
  const tableData = useMemo(() => {
    if (!trades) return [];

    return trades.map((trade) => {
      return {
        id: trade.id,
        size: trade.filled,
        price: trade.price,
        time: trade.createdAt,
        makerSide: trade.makerSide,
        sizeDecimals: getDecimalPlaces(product.lotSize || 0n),
        priceDecimals: getDecimalPlaces(product.tickSize || 0n),
      } satisfies TableData;
    });
  }, [trades, product.lotSize, product.tickSize]);

  return (
    <TradesUi
      tableData={tableData}
      isFetching={isFetching}
      isPending={isPending}
      isConnected={isConnected}
      baseTokenName={product.baseTokenName}
      quoteTokenName={product.quoteTokenName}
    />
  );
};
